import { ArrowUpRight, ExternalLink, Github, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LazyImage } from '@/components/ui/Primitives';
import { TechChipRow } from '@/components/ui/TechChip';
import { cx, formatDateRange } from '@/lib/format';
import { localized, type Locale, type Project } from '@/lib/types';
import { useI18n } from '@/store/preferences';

export function ProjectCard({ project, featured = false }: { project: Project; featured?: boolean }) {
  const { t, locale } = useI18n();
  const isLive = project.status === 'IN_PRODUCTION';

  // Closed source: offer the running site instead of a link nobody can open.
  const secondary = project.isRepoPrivate
    ? project.liveUrl
      ? { href: project.liveUrl, icon: ExternalLink, label: t.project.viewLive }
      : null
    : project.repoUrl
      ? { href: project.repoUrl, icon: Github, label: t.project.viewCode }
      : null;

  const visibleTechs = featured ? 10 : 7;

  return (
    <article
      className={cx(
        // No `overflow-hidden` here: it would clip the technology tooltips.
        // The cover image clips itself instead.
        'card-interactive group relative flex h-full flex-col',
        featured && 'lg:flex-row'
      )}
    >
      <Link
        to={`/projects/${project.slug}`}
        className="absolute inset-0 z-10"
        aria-label={project.title}
      />

      {project.coverImage ? (
        <LazyImage
          src={project.coverImage}
          alt={project.title}
          aspect={featured ? 'aspect-[1200/630] lg:aspect-auto' : 'aspect-[1200/630]'}
          className={cx(
            // Rounds itself now that the card no longer clips its children.
            'rounded-t-2xl transition-transform duration-700 ease-smooth group-hover:scale-[1.03]',
            featured && 'lg:w-[46%] lg:shrink-0 lg:rounded-l-2xl lg:rounded-tr-none'
          )}
        />
      ) : (
        <CoverFallback project={project} featured={featured} locale={locale} />
      )}

      <div className="flex flex-1 flex-col p-6 sm:p-7 lg:p-8">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span
            className={cx(
              'chip',
              isLive && 'border-emerald-500/25 bg-emerald-500/10 text-emerald-400'
            )}
          >
            {isLive && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden />}
            {t.status[project.status]}
          </span>

          {project.isRepoPrivate && (
            <span className="chip">
              <Lock className="h-3 w-3" aria-hidden />
              {t.project.privateRepo}
            </span>
          )}

          <span className="font-mono text-2xs text-faint">
            {formatDateRange(project.startDate, project.endDate, locale, t.project.present)}
          </span>
        </div>

        <h3
          className={cx(
            'font-semibold tracking-tight transition-colors duration-200 group-hover:text-accent',
            featured ? 'text-2xl sm:text-3xl' : 'text-xl'
          )}
        >
          {project.title}
        </h3>

        <p className="mt-3 flex-1 text-pretty text-sm leading-relaxed text-muted">
          {localized(project, 'summary', locale)}
        </p>

        <div className="relative z-20 mt-6">
          <TechChipRow technologies={project.technologies} max={visibleTechs} size="sm" />
        </div>

        <div className="mt-7 flex items-center justify-between gap-4 border-t border-line pt-5">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-ink">
            {t.project.overview}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>

          {secondary && (
            <a
              href={secondary.href}
              target="_blank"
              rel="noopener noreferrer"
              className="relative z-20 inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-ink"
              onClick={(event) => event.stopPropagation()}
            >
              <secondary.icon className="h-3.5 w-3.5" aria-hidden />
              {secondary.label}
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

/**
 * Stand-in for a project with no screenshot. An empty plate with the title in
 * it says nothing, so the numbers the project already carries go here instead:
 * a reader scanning the cards gets the size of the thing before they decide
 * whether to open it. Falls back to the title only when there are no metrics.
 */
function CoverFallback({
  project,
  featured,
  locale,
}: {
  project: Project;
  featured: boolean;
  locale: Locale;
}) {
  const metrics = (project.metrics ?? []).slice(0, 3);

  return (
    <div
      className={cx(
        'relative overflow-hidden rounded-t-2xl bg-elevated',
        featured
          ? 'aspect-[1200/630] lg:aspect-auto lg:w-[46%] lg:shrink-0 lg:rounded-l-2xl lg:rounded-tr-none'
          : 'aspect-[1200/630]'
      )}
    >
      <div className="absolute inset-0 grid-lines opacity-40" aria-hidden />
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(120% 90% at 50% 110%, rgb(var(--c-accent) / 0.14), transparent 65%)',
        }}
        aria-hidden
      />

      {metrics.length > 0 ? (
        <div className="relative flex h-full items-center justify-center px-6">
          <dl className="flex w-full max-w-sm items-stretch justify-center divide-x divide-line">
            {metrics.map((metric) => (
              <div key={metric.en} className="flex-1 px-3 text-center">
                <dd className="text-2xl font-semibold tracking-tight tabular text-ink sm:text-3xl">
                  {metric.value}
                </dd>
                <dt className="mt-1.5 font-mono text-2xs uppercase leading-tight tracking-[0.14em] text-faint">
                  {locale === 'es' ? metric.es : metric.en}
                </dt>
              </div>
            ))}
          </dl>
        </div>
      ) : (
        <div className="relative grid h-full place-items-center">
          <span className="font-mono text-2xs uppercase tracking-[0.28em] text-faint">
            {project.title}
          </span>
        </div>
      )}
    </div>
  );
}

/** Compact row for coursework, so small projects never outshout real work. */
export function MinorProjectRow({ project }: { project: Project }) {
  const { t, locale } = useI18n();

  return (
    <article className="group relative flex items-center gap-4 border-b border-line py-5 transition-colors last:border-0 hover:border-ink/20">
      <Link to={`/projects/${project.slug}`} className="absolute inset-0" aria-label={project.title} />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className="text-base font-medium tracking-tight transition-colors group-hover:text-accent">
            {project.title}
          </h3>
          <span className="font-mono text-2xs text-faint">
            {new Date(project.startDate).getFullYear()}
          </span>
        </div>
        <p className="mt-1 truncate text-sm text-muted">{localized(project, 'summary', locale)}</p>
      </div>

      <ul className="hidden shrink-0 gap-1.5 sm:flex">
        {project.technologies.slice(0, 2).map((tech) => (
          <li key={tech.id} className="chip">
            {tech.name}
          </li>
        ))}
      </ul>

      {project.repoUrl && !project.isRepoPrivate && (
        <a
          href={project.repoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-icon relative z-10 shrink-0"
          aria-label={`${t.project.viewCode}: ${project.title}`}
        >
          <Github className="h-4 w-4" />
        </a>
      )}
    </article>
  );
}
